/**
 * Flaechen-Bruecke zwischen einbettender Seite (Parent) und eingebetteter App (Child).
 *
 * ── WARUM EINE DRITTE BRUECKE UND NICHT EIN FELD IN DER THEME-NACHRICHT ──────
 *
 * Die Theme-Bruecke (`embed-theme.ts`) traegt den MODUS (hell/dunkel). Sie kommt
 * damit durch, weil der geteilte Keks `janeway-theme` beim LADEN mitgeht: Parent
 * und Child starten gleich, und die Nachricht schliesst nur die Luecke danach.
 * Genau das steht dort im Kopf.
 *
 * Fuer die FLAECHE gibt es keinen solchen Keks, und das ist kein Detail, sondern
 * der ganze Unterschied. Gemessen am 2026-09-16 an der laufenden
 * `contacts.janewaylabs.com/?embed=1`, drei Laeufe mit leerem Cache:
 *
 *     erstes Bild (FCP)          316–340 ms
 *     load-Ereignis              391–492 ms
 *     erster message-Listener    540–633 ms
 *
 * Der `onLoad`-Post des Einbetters (`AppEmbedArtifact`) geht also **141–150 ms
 * BEVOR die eingebettete App ueberhaupt einen Empfaenger hat** — er faellt ins
 * Leere, ohne Fehler und ohne Spur. Die Theme-Bruecke merkt das nie, weil der
 * Keks den Anfangszustand schon gesetzt hat; eine Flaeche, die nur so kaeme,
 * saesse bis zum naechsten Theme-Wechsel auf dem falschen Ton.
 *
 * Und selbst mit einem Empfaenger bliebe die falsche Flaeche 224–293 ms stehen
 * (dieselben Laeufe, FCP bis Listener). Das ist das sichtbare Umspringen.
 *
 * DESHALB ZWEI WEGE FUER EINEN WERT:
 *   1. `?flaeche=%23272727` an der Einbettungs-URL — reist mit der ANFRAGE und
 *      ist damit beim ERSTEN Bild da, so wie der Theme-Keks. Das ist der Weg,
 *      der das Umspringen verhindert; die Nachricht kann es nicht.
 *   2. `postMessage` — fuer jede spaetere Aenderung (Kit-Wechsel, Theme-Wechsel,
 *      Panel wandert). Ein Neuladen des iframes dafuer waere sichtbarer als der
 *      Fehler, den es behebt.
 *
 * ── WARUM EIN WERT UND NICHT EIN PAAR ───────────────────────────────────────
 *
 * `globals.css` haelt fuer die Seitenleiste fest: „eine Flaeche reist mit ihrem
 * Vordergrund". Ueber die iframe-Grenze gilt das genauso — nur laesst sich der
 * Vordergrund hier ABLEITEN statt uebertragen, und das ist in dieser Bibliothek
 * schon gebaut: `TradingViewWidget` misst die Flaeche unter sich und rechnet
 * Schrift, Gitter und Kartentoene aus ihrer Leuchtdichte aus. Dieselbe Rechnung
 * steht unten in `tonFuerFlaeche`.
 *
 * Warum das nicht optional ist: die naive Loesung — das Kind wird einfach
 * durchsichtig und laesst den Rahmen durch — wurde am 2026-09-16 durchgerechnet
 * und faellt genau hier um. Im hellen Janeway-Theme ist der Kit-3-Grund
 * `--jw-schale` = rgb(39,39,39), der Vordergrund der hellen App
 * `hsl(0 0% 4%)` = rgb(10,10,10):
 *
 *     Naht heute (App hell auf dunklem Rahmen)   14,3 : 1   ← der gemeldete Absatz
 *     Naht bei blosser Durchsichtigkeit           1,0 : 1   ← behoben
 *     SCHRIFT bei blosser Durchsichtigkeit        1,33 : 1  ← unlesbar
 *
 * Die Naht zu schliessen und die Schrift dabei zu verlieren ist kein Fortschritt.
 * Wer die Flaeche uebernimmt, uebernimmt den Ton.
 */

/** Query-Parameter, mit dem der Einbetter seine Flaeche an die Anfrage haengt. */
export const EMBED_FLAECHE_PARAM = "flaeche";

/** Fenster-Nachricht fuer jede Aenderung NACH dem ersten Bild. */
export interface EmbedFlaecheMessage {
  __janewayEmbedFlaeche: string;
}

/**
 * Erlaubt ist ausschliesslich `#rrggbb` — sechs Hex-Ziffern, sonst nichts.
 *
 * Der Wert kommt aus einer URL und aus einer Fensternachricht und landet in
 * einer CSS-Deklaration. `rgb(…)`, `var(…)`, `color-mix(…)` oder gar ein freier
 * String waeren damit ein Einschleusweg in den Stil der eingebetteten App —
 * `url(…)` in einem Farbwert holt Daten von fremden Servern, und ein `;` beendet
 * die Deklaration. Eine feste Form statt „was reinkommt" ist hier dieselbe
 * Entscheidung wie die feste Gebietsliste in `embed-refresh.ts`: ein unbekannter
 * Wert wird VERWORFEN, bevor er irgendwo landet.
 *
 * Kein `#rgb` (drei Ziffern) und kein `#rrggbbaa`: die Kurzform spart nichts,
 * und ein Alphakanal waere eine halbdurchsichtige Flaeche — dann stuende
 * dahinter das Weiss der iframe-Leinwand statt des Rahmens.
 */
const FLAECHE_MUSTER = /^#[0-9a-fA-F]{6}$/;

export function istFlaechenwert(wert: unknown): wert is string {
  return typeof wert === "string" && FLAECHE_MUSTER.test(wert);
}

export function isEmbedFlaecheMessage(data: unknown): data is EmbedFlaecheMessage {
  return istFlaechenwert((data as EmbedFlaecheMessage | null)?.__janewayEmbedFlaeche);
}

/**
 * Parent-Seite: meldet dem eingebetteten iframe die Flaeche, auf der es liegt.
 *
 * `targetOrigin` ist die aufgeloeste Ziel-Origin, **nie `"*"`** — woertlich
 * dieselbe Zusage wie `postEmbeddedTheme` und `postEmbeddedRefresh`. Ein leeres
 * oder auf `"*"` gesetztes Ziel wird nicht gesendet, statt es abzuschwaechen.
 */
export function postEmbeddedFlaeche(
  frame: HTMLIFrameElement | null,
  targetOrigin: string,
  flaeche: string,
): void {
  const win = frame?.contentWindow;
  if (!win) return;
  if (!targetOrigin || targetOrigin === "*") return;
  if (!istFlaechenwert(flaeche)) return;
  const message: EmbedFlaecheMessage = { __janewayEmbedFlaeche: flaeche };
  win.postMessage(message, targetOrigin);
}

/** Child-Seite: die Flaeche aus der eigenen Adresse lesen (der Weg fuers erste Bild). */
export function flaecheAusUrl(search: string): string | null {
  const wert = new URLSearchParams(search).get(EMBED_FLAECHE_PARAM);
  return istFlaechenwert(wert) ? wert.toLowerCase() : null;
}

/** Parent-Seite: den Parameter an eine Einbettungs-Adresse haengen. */
export function mitFlaecheInDerUrl(url: string, flaeche: string | null): string {
  if (!istFlaechenwert(flaeche)) return url;
  const trenner = url.includes("?") ? "&" : "?";
  return `${url}${trenner}${EMBED_FLAECHE_PARAM}=${encodeURIComponent(flaeche)}`;
}

/**
 * Die Flaeche, auf der ein Knoten TATSAECHLICH liegt: der erste Vorfahr mit
 * einer wirklich deckenden Hintergrundfarbe.
 *
 * Gemessen statt aus einem Token geraten, aus demselben Grund wie in
 * `TradingViewWidget.flaecheUnter`: die Antwort haengt am Huellen-Kit, nicht am
 * Theme. Unter „umklammert" sind Panel und seine Kinder durchsichtig
 * (`globals.css`), der Grund steht erst am Wurzelknoten der Huelle — `--card`
 * oder `--background` zu lesen traefe in drei von vier Kits daneben.
 *
 * Gesucht wird ab dem ELTERNKNOTEN des iframes: das iframe-Element selbst soll
 * die gefundene Farbe gleich tragen (sonst blitzt sie beim Laden nicht) und
 * wuerde sich sonst bei der naechsten Messung selbst antworten.
 */
/**
 * Der Wert von `--jw-flaeche-hier` an dieser Stelle, als `#rrggbb`.
 *
 * ── WARUM NICHT EINFACH `deckendeFarbe(getPropertyValue(...))` ─────────────
 *
 * Weil die Variable NICHT in `rgb()` steht. Am 2026-09-20 in `janeway-ai-chat`
 * gemessen: `--jw-flaeche-hier` am Rahmen ist `hsl(0 0% 0%)`, an der
 * Inhaltskarte `hsl(0 0% 18%)` — die 43 Themes sind in HSL geschrieben, und der
 * berechnete Wert einer Custom Property behaelt die Schreibweise, anders als
 * eine echte Farbeigenschaft wie `background-color`. `deckendeFarbe` kennt nur
 * `rgb()`, `rgba()` und `color(srgb …)`, gab also `null` zurueck, und der
 * ganze Griff fiel still auf die Vorfahrenkette zurueck.
 *
 * Das war kein Randfall: die erste Fassung dieser Funktion sah richtig aus, lag
 * im gebauten Code und tat NICHTS. Der Betreiber sah das Seitenpanel danach
 * unveraendert in Inhaltsfarbe.
 *
 * Aufgeloest wird deshalb wie ueberall sonst in dieser Bibliothek, wo eine
 * Theme-Variable zu einer echten Farbe werden muss: ueber die berechnete
 * `color` eines Messknotens. Der Browser rechnet dabei JEDEN Farbausdruck nach
 * `rgb()`/`rgba()` um — hsl, oklch und auch `color-mix()`.
 *
 * Der Knoten haengt am gefragten Element, nicht am Body: Custom Properties
 * werden vererbt, und nur dort gilt der Wert DIESER Stelle.
 */
function flaecheAusDerHuelle(start: Element | null): string | null {
  if (!start || typeof document === "undefined") return null;
  const roh = window.getComputedStyle(start).getPropertyValue("--jw-flaeche-hier").trim();
  if (!roh) return null;
  const messer = document.createElement("span");
  messer.setAttribute("aria-hidden", "true");
  messer.style.cssText = "position:absolute;width:0;height:0;visibility:hidden";
  messer.style.color = roh;
  start.appendChild(messer);
  try {
    return deckendeFarbe(window.getComputedStyle(messer).color);
  } finally {
    messer.remove();
  }
}

export function gemesseneFlaeche(start: Element | null | undefined): string | null {
  if (typeof window === "undefined") return null;

  // ── ZUERST DIE HUELLE FRAGEN, DANN DIE VORFAHRENKETTE ─────────────────────
  //
  // Die Kette allein nimmt die ERSTE deckende Flaeche ueber dem Startknoten —
  // und das ist im Seitenpanel die Artefaktkarte, nicht das Panel. Am
  // 2026-09-20 gemeldet: „wenn ich im AI-Chat Kalender oeffne, ist der wieder
  // farbig wie der Contentbereich, obwohl er den Hintergrund wie Contentrahmen
  // und Sidebar haben sollte." Genau so war es: gemessen wurde die Karte,
  // weitergereicht ihre Farbe, und der Gast malte sich gehorsam in
  // Inhaltsfarbe.
  //
  // `--jw-flaeche-hier` ist die Antwort der Huelle auf „welche Flaeche gilt an
  // dieser Stelle" (Kontrakt §1.1a). Im Panel (`data-jw-nebenkarte`) ist das
  // die GRUNDflaeche — dieselbe wie Rahmen und Seitenleiste. Eine Karte, die
  // sich darauflegt, aendert daran nichts; sie ist Inhalt des Panels, nicht
  // seine Flaeche.
  //
  // Die Kette bleibt als Rueckfall: ausserhalb der Huelle (Showroom, fremdes
  // Dokument, Test) gibt es die Variable nicht, und dann ist „worauf liege ich"
  // weiterhin die beste verfuegbare Antwort.
  const ausDerHuelle = flaecheAusDerHuelle(start ?? null);
  if (ausDerHuelle) return ausDerHuelle;

  for (let knoten: Element | null = start ?? null; knoten; knoten = knoten.parentElement) {
    const farbe = deckendeFarbe(window.getComputedStyle(knoten).backgroundColor);
    if (farbe) return farbe;
  }
  return null;
}

/**
 * Der Wert EINER Marke an dieser Stelle im Baum, als `#rrggbb`.
 *
 * Das Gegenstueck zu `gemesseneFlaeche`: jene beantwortet „worauf liege ich",
 * diese „was sagt das Theme hier zu `--destructive`". Beides braucht, wer einem
 * FREMDEN Dokument Farben hinueberreicht — ein Stripe-iframe sieht unsere
 * Variablen nicht, es bekommt feste Werte oder gar nichts.
 *
 * ── WARUM DIE VORFAHRENKETTE UND NICHT `var(--x)` AUF EINEM PROBEKNOTEN ─────
 *
 * Der naheliegende Griff — ein Element mit `background-color: var(--x)` setzen
 * und die berechnete Farbe zurueckzulesen — laeuft im Browser, ist aber in
 * jsdom nicht pruefbar: dort bleibt `backgroundColor` woertlich
 * `"var(--destructive)"` stehen, und Custom Properties werden in
 * `getComputedStyle` gar nicht vererbt (gemessen 2026-09-18). Ein Waechter
 * koennte die Mechanik dann nur behaupten.
 *
 * Die Kette hinaufzulesen laeuft dagegen in BEIDEN Welten: im Browser trifft
 * schon der erste Knoten, weil die Vererbung dort in der berechneten
 * Eigenschaft steht; in jsdom findet die Schleife den Knoten, der sie gesetzt
 * hat. Und `hsl(…)` normalisieren beide gleich — genau die Schreibweise, in der
 * die 43 Themes stehen.
 *
 * `null` heisst „kein brauchbarer Wert": nicht gesetzt, halbdurchsichtig oder
 * in einer Form, die hier niemand aufloest (`color-mix(…)` normalisiert kein
 * CSSOM zur Farbe). Der Aufrufer laesst den Schluessel dann weg, statt einen
 * Wert zu raten.
 */
export function gemesseneTokenfarbe(name: string, knoten: Element | null | undefined): string | null {
  if (typeof window === "undefined") return null;
  for (let k: Element | null = knoten ?? null; k; k = k.parentElement) {
    const roh = window.getComputedStyle(k).getPropertyValue(name).trim();
    if (!roh) continue;
    const farbe = alsHex(roh);
    if (farbe) return farbe;
  }
  return null;
}

/**
 * Ein CSS-Farbwert → `#rrggbb`, ohne den Knoten in den Baum zu haengen.
 *
 * Das CSSOM normalisiert eine Farbe schon beim ZUWEISEN an die Stil-Deklaration
 * — dafuer braucht das Element weder Eltern noch Layout. Ein `appendChild` waere
 * hier ein sichtbarer Nebeneffekt fuer eine reine Umrechnung.
 */
function alsHex(wert: string): string | null {
  const direkt = deckendeFarbe(wert);
  if (direkt) return direkt;
  const probe = document.createElement("span");
  probe.style.backgroundColor = wert;
  return deckendeFarbe(probe.style.backgroundColor);
}

/**
 * `rgb(…)`/`rgba(…)`/`color(srgb …)` → `#rrggbb`, aber nur wenn WIRKLICH deckend.
 *
 * Eine halbdurchsichtige Flaeche ist keine Antwort: darunter liegt noch etwas,
 * und die Suche muss weiterlaufen. Ein unlesbarer Wert wird ueber
 * `NaN >= 1 === false` zu „nicht deckend" und faellt auf denselben Weg.
 */
function deckendeFarbe(wert: string): string | null {
  const treffer =
    /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+)(%)?)?\s*\)$/.exec(wert) ??
    /^color\(\s*srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+)(%)?)?\s*\)$/.exec(wert);
  if (!treffer) return null;
  const srgb = wert.startsWith("color(");
  const alpha = treffer[4] === undefined ? 1 : Number(treffer[4]) / (treffer[5] ? 100 : 1);
  if (!(alpha >= 1)) return null;
  const faktor = srgb ? 255 : 1;
  return `#${[1, 2, 3]
    .map((i) => Math.max(0, Math.min(255, Math.round(Number(treffer[i]) * faktor))))
    .map((c) => c.toString(16).padStart(2, "0"))
    .join("")}`;
}

export interface FlaechenTon {
  /** Dunkle Flaeche → heller Vordergrund. */
  dunkel: boolean;
  /** Die Custom Properties, die die eingebettete App setzen muss. */
  variablen: Record<string, string>;
}

/**
 * Was eine uebernommene Flaeche nach sich zieht.
 *
 * Die Schwelle 0,179 ist die relative Leuchtdichte, bei der Schwarz und Weiss
 * gleich viel Kontrast haben — dieselbe Zahl und dieselbe Rechnung wie in
 * `TradingViewWidget.darstellungFuerHintergrund`. **Sie gehoert hierher und
 * nicht dorthin:** eine Farbregel, die an zwei Stellen steht, driftet. Das
 * Widget sollte sie importieren (Anmerkung fuer die Sitzung, der die Datei
 * gehoert).
 *
 * ── WELCHE TOKEN, UND WARUM NICHT MEHR ──────────────────────────────────────
 *
 * Gesetzt werden die FLAECHEN-Token und die Schrift darauf. Das ist genau die
 * Menge, die sonst aussieht, als sei die App kaputt: eine weisse Karte auf
 * schwarzem Grund faellt mehr auf als die Naht, die man gerade geschlossen hat.
 *
 * `--primary` bleibt BEWUSST unberuehrt. Es ist die Marken- und Handlungsfarbe,
 * keine Flaeche; sie auf Schwarz oder Weiss zu setzen naehme jedem Knopf seine
 * Bedeutung. `TradingViewWidget` ueberschreibt es, weil ein Kursdiagramm keine
 * Knoepfe hat — eine eingebettete App schon. Aus demselben Grund bleiben
 * `--destructive` und `--sidebar-primary` stehen: auch sie tragen Bedeutung,
 * nicht Flaeche.
 *
 * Die Abstufungen (6/10/14/18 %) sind dieselbe Geste wie die Gitterfarbe im
 * Widget: ein Stueck Richtung Gegenpol, damit Karte, Rand und Daempfung zum
 * neuen Grund gehoeren statt aus dem alten Theme stehenzubleiben.
 *
 * ── `--secondary` FEHLTE, UND DAS SAH AUS WIE EIN FEHLER DER APP ────────────
 *
 * Es ist eine FLAECHE — eine gefuellte Marke, ein Symbolfeld, ein Schalter —
 * und stand trotzdem nicht in dieser Liste. Es blieb damit auf dem Wert des
 * GASTES, waehrend alles um es herum die Flaeche des Wirts uebernahm. Am
 * 2026-09-21 im echten Panel von `chat.janewaylabs.com` gemessen, im iframe
 * selbst ausgelesen:
 *
 *   Theme         Panel gibt   --background   --card     --secondary
 *   janeway hell  #000000      #000000        #1a1a1a    #ebebeb
 *   mono hell     #242424      #242424        #313131    #ebebeb
 *
 * Die dritte Spalte ist der Befund: sie bewegt sich NICHT. Zwei verschiedene
 * Wirtsflaechen, derselbe Wert — das ist der Beweis, dass niemand ihn
 * uebersetzt, und nicht bloss ein unguenstiger Ton. Gemalt wurde er auf
 * `automations/agents` an 21, auf `automations/workflows` an fuenf Stellen im
 * gerenderten Inhalt: helle Kacheln auf schwarzem Panel.
 *
 * WELCHEN SCHRITT ES BEKOMMT, IST GEMESSEN, NICHT GEWAEHLT. Ueber alle 43
 * Themes in beiden Modi (86 Faelle, `lib/themes.ts`) liegt `secondary` gegen
 * `background` im Median bei 1,248 : 1 — `accent` bei 1,257, `muted` bei 1,140,
 * `card` bei 1,058. In 78 von 86 Faellen ist `accent` sein naechster Nachbar.
 * Es folgt deshalb `rand` (der 1,5-Schritt), genau wie `--accent`, und nicht
 * `karte`.
 *
 * `--sidebar-accent` ist derselbe Fall und aus demselben Grund mit dabei: die
 * Leiste ist eingebettet ausgeblendet, ihre Token faerben aber Bauteile, die
 * bleiben — `bg-sidebar-accent` steht an 47 Stellen in vier Repos (notes 25,
 * ai-chat 9, gui-elements 7, mail-client 6).
 */
export function tonFuerFlaeche(hex: string): FlaechenTon {
  const kanaele = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const [r, g, b] = kanaele.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const dunkel = 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.179;
  const pol = dunkel ? 255 : 0;
  const schritt = (anteil: number) =>
    `#${kanaele
      .map((c) => Math.round(c + (pol - c) * anteil).toString(16).padStart(2, "0"))
      .join("")}`;
  const schrift = dunkel ? "#ffffff" : "#000000";

  // ── GESUCHT STATT GESTUFT ────────────────────────────────────────────────
  //
  // Hier standen feste Schritte: Karte 6 %, gedaempft 10 %, Akzent 14 %, Rand
  // 18 %. Genau dieses Muster ist in der Huelle ueber 43 Themes durchgefallen —
  // 18 % Rand trug in acht Themes weniger als 1,5 : 1, und ein fester Anteil
  // fuer gedaempften Text hielt nirgends ueber alle Flaechen. Betreiber,
  // 2026-09-18: „da gibt's mehrmals Kontrastfehler, nicht alle Apps ziehen mit."
  //
  // Ein fester Schritt kann das auch nicht: wie weit man gehen MUSS, haengt an
  // der Flaeche, die der Wirt schickt, und die ist beliebig. Deshalb dieselbe
  // Suche wie in `themes.ts` — der kleinste Schritt, der die Zusage erreicht.
  const lum = (h: string) => {
    const [r2, g2, b2] = [1, 3, 5]
      .map((i) => parseInt(h.slice(i, i + 2), 16) / 255)
      .map((s2) => (s2 <= 0.03928 ? s2 / 12.92 : ((s2 + 0.055) / 1.055) ** 2.4));
    return 0.2126 * r2! + 0.7152 * g2! + 0.0722 * b2!;
  };
  const kontrast = (a: string, b: string) => {
    const [la, lb] = [lum(a), lum(b)];
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  };
  /** Der kleinste Schritt vom Grund weg, der gegen ihn `ziel` erreicht. */
  const sucheSchritt = (ziel: number, hoechstens = 0.95) => {
    for (let a = 0.02; a <= hoechstens; a += 0.02) {
      const k = schritt(a);
      if (kontrast(k, hex) >= ziel) return k;
    }
    return schritt(hoechstens);
  };
  /** Gedaempfter Text: von der vollen Schrift zurueck, so weit es traegt. */
  const sucheGedaempft = (ziel: number) => {
    for (let a = 100; a >= 30; a -= 2) {
      const k = `#${[0, 1, 2]
        .map((i) => {
          const ton = dunkel ? 255 : 0;
          const misch = Math.round(kanaele[i]! + (ton - kanaele[i]!) * (a / 100));
          return misch.toString(16).padStart(2, "0");
        })
        .join("")}`;
      if (kontrast(k, hex) >= ziel) return k;
    }
    return schrift;
  };

  // Die Zusagen: Text 4,5 : 1 (WCAG 1.4.3), Ring 3,0 (1.4.11), Kante 1,5
  // (`KANTE_MIN`), Flaeche darauf 1,15 — genug, um als eigene zu lesen.
  const karte = sucheSchritt(1.15);
  /**
   * DIE FUELLUNG IST KEINE KANTE — deshalb eine eigene Sprosse.
   *
   * `--accent`, `--secondary` und `--sidebar-accent` standen bis 0.493.2 auf
   * `rand`, also auf derselben Sprosse wie `--border` und `--input`. Das sind
   * zwei verschiedene Zusagen: eine Linie muss man SEHEN (1,5), eine Flaeche
   * soll sich nur absetzen.
   *
   * Ueber alle 86 Theme/Modus-Faelle gegen `--background` gemessen (2026-09-21):
   * `secondary` median 1,248 / max 1,495 · `accent` median 1,257 / max 1,662 ·
   * `border` median 1,912. Die 1,5-Sprosse traf die Fuellungen damit auf ihrem
   * realen MAXIMUM statt auf dem Median.
   */
  const fuellung = sucheSchritt(1.25);
  const rand = sucheSchritt(1.5);
  const ring = sucheSchritt(3.0);
  const gedaempft = sucheGedaempft(4.5);

  return {
    dunkel,
    variablen: {
      "--background": hex,
      "--foreground": schrift,
      // ── `--card` IST DIE FLAECHE, NICHT EINE STUFE DARUEBER ──────────────
      //
      // Eingebettet gibt es genau EINE Flaeche: die des Panels. Eine Karte ist
      // dort kein schwebendes Ding, sondern ein Block der Seite — sie hebt sich
      // durch ihre KANTE ab (`--border` ist uebersetzt), nicht durch einen
      // eigenen Ton.
      //
      // Bis 0.490.0 stand hier `karte`, also die 1,15-Sprosse. Auf einem
      // schwarzen Panel faellt das kaum auf (#000000 → #1a1a1a); auf einer
      // getoenten Flaeche wird daraus ein sichtbar anderer Ton. Betreiber,
      // 2026-09-21: „Kalender ist immer noch grau anstatt dieses dunkle Blau."
      // Gemessen mit Panel `#1e293b`: `--card` war `#2c3647` — ein Blaugrau
      // neben dem Blau der Seitenleiste.
      //
      // Warum es bei contacts nicht auffiel und bei calendar sofort: `bg-card`
      // steht in `janeway-calendar/components` an 14 Stellen (jede Tageszelle
      // des Monatsrasters), in `janeway-contacts/components` an EINER. Der
      // Fehler war ueberall gleich, nur die Flaeche, auf der er sichtbar wird,
      // nicht.
      //
      // `--popover` bleibt auf der Stufe: ein Popup liegt wirklich ueber der
      // Seite, und ohne eigenen Ton verschwaende es darin.
      "--card": hex,
      "--card-foreground": schrift,
      "--popover": karte,
      "--popover-foreground": schrift,
      "--muted": karte,
      "--muted-foreground": gedaempft,
      "--accent": fuellung,
      "--accent-foreground": schrift,
      // Dieselbe Sprosse wie `--accent`: ueber 86 Theme-Faelle gemessen ist das
      // sein naechster Nachbar (s. Kopf). Eine eigene Stufe waere eine zweite
      // Rechnung fuer dieselbe Flaeche.
      "--secondary": fuellung,
      "--secondary-foreground": schrift,
      "--border": rand,
      "--input": rand,
      // `--ring` fehlte ganz. Er blieb damit auf dem Wert des GASTES, waehrend
      // die Flaeche unter ihm die des Wirtes war — ein Fokusring, der auf einer
      // fremden Flaeche liegt und fuer eine andere bemessen wurde.
      "--ring": ring,
      // Die Leiste ist im Einbettungsfall ausgeblendet (`useIsEmbedded`), aber
      // ihre Token faerben auch Bauteile, die bleiben — ohne sie stuende ein
      // Rest des alten Themes mitten im neuen Ton.
      "--sidebar": hex,
      "--sidebar-foreground": schrift,
      "--sidebar-border": rand,
      // `--sidebar-accent` ist eine Flaeche auf der Leiste, `--sidebar-ring`
      // ihr Fokusring — beide gehoeren zu `--sidebar`, das hier schon die
      // Wirtsflaeche traegt. Ohne sie stuende genau darauf noch der alte Ton.
      // `--sidebar-primary` bleibt aussen vor, wie `--primary`: Bedeutung,
      // keine Flaeche.
      "--sidebar-accent": fuellung,
      "--sidebar-accent-foreground": schrift,
      "--sidebar-ring": ring,
    },
  };
}

/**
 * Die abgeleiteten Token auf einen Wurzelknoten schreiben.
 *
 * Inline am Element, genau wie `ColorThemeProvider` sein Theme schreibt — so
 * gewinnen sie gegen die Kaskade und komponieren mit dem Theme, statt es zu
 * ersetzen. Was hier nicht steht, kommt weiter aus dem Theme.
 */
/**
 * DIE VARIABLEN, DIE IM EMBED DEM WIRT GEHOEREN — und die der Gast deshalb
 * nicht ueberschreiben darf.
 *
 * ── WARUM ES DIESE LISTE BRAUCHT ────────────────────────────────────────────
 *
 * Die eingebettete App faehrt ihren EIGENEN `ColorThemeProvider`, und der
 * schreibt nach der Hydration alle Theme-Token inline an `<html>` — auch die
 * Flaechen. Die Wache in `useEmbeddedFlaecheBridge` stellt sie danach wieder
 * her, der Endzustand stimmt also. Nur ist der Weg dorthin SICHTBAR:
 * `globals.css` legt unter `data-theme-fx="soft"` eine 400-ms-Transition auf
 * `background-color` (mit `!important`, also auch auf `*`). Jede Schreibung des
 * Gastes startet damit eine Ueberblendung zu SEINER Farbe, die die Wache
 * sofort wieder umkehrt — ein Farbwackeln bei jedem Oeffnen des Panels und bei
 * jedem Theme-Schreibvorgang darin.
 *
 * Am 2026-09-21 fiel in einer Messreihe ueber vier nacheinander geoeffnete
 * Apps genau ein Zwischenwert an (`#363451`) — weder `--jw-schale` (`#0a0915`)
 * noch `--jw-tafel` (`#2e2c3f`) noch eine Sprosse darauf, also kein Token,
 * sondern ein Punkt IN einer laufenden Ueberblendung.
 *
 * Die Wache bleibt trotzdem: sie faengt, was hier durchrutscht. Aber der Umweg
 * gehoert gar nicht erst gegangen.
 *
 * **Die Liste ist bewusst ausgeschrieben und nicht aus `tonFuerFlaeche`
 * abgeleitet.** Sie wird an einer Stelle gebraucht, die diese Datei nicht
 * importieren soll (der Provider laeuft in jeder App), und eine Ableitung zur
 * Laufzeit haette einen Aufruf mit einem erfundenen Hex gebraucht. Dass beide
 * deckungsgleich sind, haelt `embed-flaeche.test.ts` fest — Name fuer Name.
 */
export const EMBED_FLAECHEN_VARIABLEN: readonly string[] = [
  "--background", "--foreground",
  "--card", "--card-foreground",
  "--popover", "--popover-foreground",
  "--muted", "--muted-foreground",
  "--accent", "--accent-foreground",
  "--secondary", "--secondary-foreground",
  "--border", "--input", "--ring",
  "--sidebar", "--sidebar-foreground", "--sidebar-border",
  "--sidebar-accent", "--sidebar-accent-foreground", "--sidebar-ring",
];

/**
 * Traegt dieses Dokument eine uebernommene Flaeche?
 *
 * Die Marke setzt `wendeFlaecheAn` und raeumt `entferneFlaeche` weg — sie gilt
 * also genau so lange, wie die Uebernahme wirklich steht.
 */
export function flaecheIstUebernommen(wurzel: HTMLElement): boolean {
  return istFlaechenwert(wurzel.dataset.jwEmbedFlaeche);
}

export function wendeFlaecheAn(wurzel: HTMLElement, flaeche: string): void {
  if (!istFlaechenwert(flaeche)) return;
  const { variablen } = tonFuerFlaeche(flaeche);
  for (const [name, wert] of Object.entries(variablen)) {
    wurzel.style.setProperty(name, wert);
  }
  wurzel.dataset.jwEmbedFlaeche = flaeche.toLowerCase();
}

/**
 * Die Uebernahme wieder abnehmen — und nur die eigenen Namen, nie den ganzen
 * Stil: auf demselben Knoten stehen die Theme-Variablen von
 * `ColorThemeProvider`. Ein `style=""` naehme sie mit und liesse die App grau
 * zurueck.
 */
export function entferneFlaeche(wurzel: HTMLElement): void {
  for (const name of Object.keys(tonFuerFlaeche("#000000").variablen)) {
    wurzel.style.removeProperty(name);
  }
  delete wurzel.dataset.jwEmbedFlaeche;
}

/**
 * Das Skript fuer den Dokumentkopf — der einzige Weg, die Flaeche VOR dem
 * ersten Bild zu setzen.
 *
 * Ein Hook laeuft nach der Hydration, und die liegt gemessen 224–293 ms hinter
 * dem ersten Bild (s. Kopf dieser Datei). Genau diese Spanne ist das sichtbare
 * Umspringen. Ein blockierendes Inline-Skript im Kopf laeuft davor — dasselbe
 * Muster, mit dem Themes ihr Aufblitzen verhindern.
 *
 * ── WARUM DIE RECHNUNG HIER EIN ZWEITES MAL STEHT ───────────────────────────
 *
 * Das Skript laeuft, bevor irgendein Bundle da ist; es kann nichts importieren.
 * Der naheliegende Griff — `tonFuerFlaeche.toString()` in den Text schreiben und
 * so nur EINE Fassung halten — ist am 2026-09-16 im Browser gemessen worden und
 * faellt lautlos um: **esbuild instrumentiert die transpilierte Funktion mit
 * seinem eigenen Helfer `__name(...)`.** Der Quelltext, den `toString()`
 * zurueckgibt, verweist damit auf einen Namen, den es im Kopf-Skript nicht gibt;
 * das `catch` unten verschluckt den `ReferenceError`, und die Flaeche kommt
 * einfach nie an — ohne Fehler, ohne Spur, genau das Ausfallmuster, gegen das
 * diese Bruecke gebaut ist.
 *
 * Der Unit-Test hat das ZUERST NICHT GEMELDET, und das ist die eigentliche
 * Lehre: unter vitest steht `__name` im Geltungsbereich, im Browser nicht. Ein
 * Waechter, der im selben Werkzeug laeuft wie der Fehler, kann ihn nicht sehen.
 * `embed-flaeche-vorpaint.test.ts` blendet den Helfer deshalb ausdruecklich aus
 * (`__name` als `undefined` uebergeben) und bildet damit den Browser nach.
 *
 * Die Dublette ist also bewusst, und sie ist BEWIESEN gleichwertig statt
 * behauptet: derselbe Waechter vergleicht beide Fassungen ueber eine Reihe von
 * Farben Wert fuer Wert. Wer eine der beiden aendert, faellt dort auf.
 *
 * Das Muster steht ebenfalls als Literal und nicht als Verweis — aus demselben
 * Grund, und derselbe Waechter prueft, dass es dieselben Werte annimmt und
 * ablehnt wie `istFlaechenwert`.
 */
export function embedFlaecheVorpaintSkript(): string {
  return [
    "(function(){try{",
    "if(window.self===window.top)return;",
    `var w=new URLSearchParams(location.search).get(${JSON.stringify(EMBED_FLAECHE_PARAM)});`,
    "if(!w||!/^#[0-9a-fA-F]{6}$/.test(w))return;",
    "var k=[1,3,5].map(function(i){return parseInt(w.slice(i,i+2),16)});",
    "var l=k.map(function(c){var s=c/255;return s<=0.03928?s/12.92:Math.pow((s+0.055)/1.055,2.4)});",
    "var d=0.2126*l[0]+0.7152*l[1]+0.0722*l[2]<0.179;",
    "var p=d?255:0;",
    "var st=function(a){return '#'+k.map(function(c){",
    "return Math.round(c+(p-c)*a).toString(16).padStart(2,'0')}).join('')};",
    "var f=d?'#ffffff':'#000000';",
    // DIESELBE SUCHE WIE IN `tonFuerFlaeche`, nur als Inline-Skript. Dass sie
    // zweimal dasteht, ist unvermeidlich — dieses hier laeuft VOR jedem Bild
    // und darf nichts importieren. Dass beide dasselbe ergeben, haelt
    // `embed-flaeche-vorpaint.test.ts` fest, und genau der hat beim Umbau auf
    // die Suche angeschlagen: die feste Stufung stand noch hier, als sie
    // drueben schon weg war.
    "var lu=function(h){var q=[1,3,5].map(function(i){var s2=parseInt(h.slice(i,i+2),16)/255;",
    "return s2<=0.03928?s2/12.92:Math.pow((s2+0.055)/1.055,2.4)});",
    "return 0.2126*q[0]+0.7152*q[1]+0.0722*q[2]};",
    "var ko=function(a,b){var x=lu(a),y=lu(b);",
    "return (Math.max(x,y)+0.05)/(Math.min(x,y)+0.05)};",
    "var su=function(z){for(var a=0.02;a<=0.95;a+=0.02){var c=st(a);",
    "if(ko(c,w)>=z)return c}return st(0.95)};",
    "var gd=function(z){for(var a=100;a>=30;a-=2){",
    "var c='#'+k.map(function(q){return Math.round(q+(p-q)*(a/100)).toString(16).padStart(2,'0')}).join('');",
    "if(ko(c,w)>=z)return c}return f};",
    "var ka=su(1.15);var fu=su(1.25);var r=su(1.5);var ri=su(3.0);var gm=gd(4.5);",
    "var v={'--background':w,'--foreground':f,'--card':w,'--card-foreground':f,",
    "'--popover':ka,'--popover-foreground':f,'--muted':ka,",
    "'--muted-foreground':gm,",
    "'--accent':fu,'--accent-foreground':f,'--secondary':fu,'--secondary-foreground':f,",
    "'--border':r,'--input':r,'--ring':ri,",
    "'--sidebar':w,'--sidebar-foreground':f,'--sidebar-border':r,",
    "'--sidebar-accent':fu,'--sidebar-accent-foreground':f,'--sidebar-ring':ri};",
    "var s=document.documentElement.style;",
    "for(var n in v)s.setProperty(n,v[n]);",
    "document.documentElement.setAttribute('data-jw-embed-flaeche',w.toLowerCase());",
    "}catch(e){}})()",
  ].join("");
}
